import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, IMPERSONATION_KEY, IMPERSONATION_META_KEY } from "./api";
import { supabase } from "./supabase";

const AuthCtx = createContext(null);

// "#1F6FEB" → "31, 111, 235". Exposed as `--primary-hex-rgb` so CSS can build
// `rgba(var(--primary-hex-rgb), 0.06)` washes on the body without JS on paint.
function hexToRgbTriplet(hex, fallback) {
  const clean = /^#?[0-9a-fA-F]{6}$/.test(hex || "") ? hex.replace("#", "") : fallback;
  const int = parseInt(clean, 16);
  return `${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}`;
}

export function applyBranding(brand, accent) {
  const root = document.documentElement;
  const primary = brand || "#1F6FEB";
  const secondary = accent || "#F59E0B";
  root.style.setProperty("--primary-hex", primary);
  root.style.setProperty("--accent-hex", secondary);
  root.style.setProperty("--primary-hex-rgb", hexToRgbTriplet(primary, "1F6FEB"));
  root.style.setProperty("--accent-hex-rgb", hexToRgbTriplet(secondary, "F59E0B"));
}

export function AuthProvider({ children }) {
  const [agent, setAgent] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAgent = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setAgent(data);
      applyBranding(data.brand_color, data.accent_color);
    } catch { setAgent(null); }
  }, []);

  useEffect(() => {
    // Bootstrap impersonation BEFORE the first /auth/me hit — the api
    // interceptor picks up the token from sessionStorage, so `loadAgent()`
    // will return the target agent instead of the admin. The `#imp=`
    // fragment is used (not a query string) so it's never logged.
    if (typeof window !== "undefined" && window.location.hash.startsWith("#imp=")) {
      try {
        const payload = JSON.parse(decodeURIComponent(window.location.hash.slice(5)));
        sessionStorage.setItem(IMPERSONATION_KEY, payload.token);
        sessionStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify({
          target: payload.target,
          actor: payload.actor,
          expires_at: payload.expires_at,
        }));
        window.history.replaceState({}, "", window.location.pathname);
      } catch (e) { console.error("[auth] impersonation bootstrap failed", e); }
    }

    // Public-preview short-circuit: if a preview JWT is already in
    // sessionStorage (stashed by /preview/felecia-demo), skip the
    // Supabase session lookup entirely — preview visitors don't have
    // one, and asking Supabase for it would just add a wasted round-trip.
    const impTok = (() => { try { return sessionStorage.getItem(IMPERSONATION_KEY); } catch { return null; } })();
    if (impTok && impTok.startsWith("iKP.")) {
      loadAgent().finally(() => setLoading(false));
      return () => {};
    }

    // Initial session from stored refresh token
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session) await loadAgent();
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_e, newSession) => {
      setSession(newSession);
      if (newSession) await loadAgent();
      else setAgent(null);
    });
    return () => sub.subscription.unsubscribe();
  }, [loadAgent]);

  const login = async (email, password) => {
    // Two race conditions this function has to close:
    //   1. `signInWithPassword` resolves once the token is minted, but
    //      `onAuthStateChange` (which persists the session to
    //      localStorage + fires our subscriber) is queued as a
    //      microtask. If the login page redirects to `/` synchronously
    //      after `login()` resolves, the very next `api.get(...)` can
    //      race the state-change listener and go out WITHOUT an
    //      Authorization header → intermittent 401.
    //   2. Even after the listener fires, our interceptor calls
    //      `supabase.auth.getSession()` which does another async round;
    //      that's fine as long as the session is actually on disk.
    //
    // Fix: after signIn, block on `getSession()` until Supabase
    // confirms a session AND load the agent profile via /auth/me
    // BEFORE returning. Callers can safely redirect on resolve —
    // subsequent requests will always carry a token.
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
    // Explicitly set the returned session so `getSession()` in the
    // interceptor returns immediately on the very next call, without
    // waiting for the auth-state-change listener to flush.
    if (data?.session) {
      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      setSession(data.session);
    }
    // Warm /auth/me so `agent` is populated before <Protected> lets
    // downstream components mount and start firing requests. Even if
    // /auth/me itself races, we've already got a token in memory now.
    await loadAgent();
  };

  const register = async ({ email, password, full_name, phone, brand_color, referral_code }) => {
    // Signup collects Name / Email / Phone / Password. Password is sent to
    // the backend which forwards it to Supabase's admin API — the response
    // gives us an instant session so the agent never sees an email-confirm
    // page. Future logins from other devices use "Forgot password?" if the
    // user forgets.
    const { data } = await api.post("/auth/signup", { email, password, full_name, phone, brand_color: brand_color || "#1F6FEB", referral_code });
    // Manually set the Supabase client session so onAuthStateChange fires and interceptors work
    const { error } = await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
    if (error) throw new Error(error.message);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  // Optimistic local patch — merge a partial into the cached agent so the UI
  // reflects an in-flight upload/save without waiting for a full /auth/me
  // round-trip. Server refresh still happens right after via `refresh()`.
  const patchAgent = useCallback((partial) => {
    setAgent(prev => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const value = useMemo(
    () => ({ agent, session, loading, login, register, logout, refresh: loadAgent, patchAgent }),
    // login/register/logout are stable within a render tree (they only
    // reference module-singleton `api` + `supabase`), so we intentionally
    // depend on the state values that actually change.
     
    [agent, session, loading, loadAgent, patchAgent],
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
